import { memo, useState, useCallback } from 'react';
import { Sparkles, Loader2, ChevronDown, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { STYLE_PRESETS_MAIN, STYLE_PRESETS_MORE } from '../../data/stylePresets';
import LoadingView from './LoadingView';
import RightPanel from './RightPanel';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const ALL_RETRY_STYLES = [...STYLE_PRESETS_MAIN, ...STYLE_PRESETS_MORE];

const ResultView = memo(({ generatedHtml, businessName, businessType, style, notes, onSignup, onClose, onRetry, isLoggedIn, regenerating, retryError, progress, loadingMsg }) => {
  const [resultTab, setResultTab] = useState('preview');
  const [showRetry, setShowRetry] = useState(false);

  // Inline auth state
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('signup'); // 'signup' | 'login'
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authName, setAuthName] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showPw, setShowPw] = useState(false);

  const handleSignup = useCallback(() => {
    if (isLoggedIn) {
      // Already logged in — save directly
      if (onSignup) onSignup({ business_name: businessName, business_type: businessType, style, notes, html: generatedHtml });
    } else {
      // Show inline auth form
      setShowAuth(true);
    }
  }, [onSignup, businessName, businessType, style, notes, generatedHtml, isLoggedIn]);

  const handleInlineAuth = useCallback(async (e) => {
    e.preventDefault();
    if (authLoading) return;
    setAuthError('');
    setAuthLoading(true);

    try {
      // Step 1: Auth (signup or login)
      const endpoint = authMode === 'login' ? '/auth/login' : '/auth/signup';
      const body = authMode === 'login'
        ? { email: authEmail, password: authPassword }
        : { email: authEmail, password: authPassword, name: authName };
      await axios.post(`${API}${endpoint}`, body, { withCredentials: true });

      // Step 2: Save site
      const siteRes = await axios.post(`${API}/sites`, {
        business_name: businessName || 'My Studio',
        business_type: businessType || 'photographer',
        style: style || 'romantic',
        notes: notes || '',
        html: generatedHtml,
      }, { withCredentials: true });

      // Step 3: Navigate to builder (full page load to pick up auth state)
      window.location.href = `/builder/${siteRes.data.site.id}`;
    } catch (err) {
      const detail = err.response?.data?.detail;
      setAuthError(typeof detail === 'object' ? detail.message : detail || 'Something went wrong. Try again.');
      setAuthLoading(false);
    }
  }, [authMode, authEmail, authPassword, authName, authLoading, businessName, businessType, style, notes, generatedHtml]);

  const handleRetryPreset = useCallback((prompt) => {
    const el = document.getElementById('retry-vibe-input');
    if (el) el.value = prompt;
    onRetry(prompt);
  }, [onRetry]);

  if (regenerating) {
    return (
      <div className="w-full h-full flex">
        <div className="w-full lg:w-[480px] h-full flex-col overflow-y-auto flex-shrink-0 p-6 lg:p-8" style={{ borderRight: '1px solid #e8e3dc', background: '#faf8f5' }}>
          <LoadingView progress={progress} loadingMsg={loadingMsg} loading={true} />
        </div>
        <RightPanel loading={true} progress={progress} loadingMsg={loadingMsg} />
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      {/* Mobile tab bar */}
      <div className="flex lg:hidden" style={{ borderBottom: '1px solid #e8e3dc', flexShrink: 0 }}>
        <button onClick={() => setResultTab('preview')} style={{ flex: 1, padding: '12px', fontFamily: 'var(--sans)', fontSize: '10px', letterSpacing: '.1em', textTransform: 'uppercase', background: 'none', border: 'none', cursor: 'pointer', color: resultTab === 'preview' ? '#C9A84C' : '#8c8782', borderBottom: resultTab === 'preview' ? '2px solid #C9A84C' : '2px solid transparent' }}>
          Preview Site
        </button>
        <button onClick={() => setResultTab('info')} style={{ flex: 1, padding: '12px', fontFamily: 'var(--sans)', fontSize: '10px', letterSpacing: '.1em', textTransform: 'uppercase', border: 'none', cursor: 'pointer', color: resultTab === 'info' ? '#000' : '#C9A84C', background: resultTab === 'info' ? '#C9A84C' : 'rgba(201,168,76,0.08)', borderBottom: resultTab === 'info' ? '2px solid #C9A84C' : '2px solid transparent', fontWeight: 700 }}>
          What&apos;s Next
        </button>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* LEFT panel */}
        <div className={`${resultTab === 'preview' ? 'hidden lg:flex' : 'flex'} w-full lg:w-[420px] h-full flex-col p-6 lg:p-8 overflow-y-auto flex-shrink-0`} style={{ borderRight: '1px solid #e8e3dc', background: '#faf8f5', position: 'relative' }}>
          <span className="hidden lg:block" style={{ fontFamily: 'var(--serif)', fontSize: '22px', color: '#1F1D1B', marginBottom: '28px', letterSpacing: '-.02em' }}>
            golden<em style={{ fontStyle: 'italic', color: '#C9A84C' }}>hour</em>
          </span>

          {retryError && !regenerating && (
            <div style={{ padding: '10px 14px', background: 'rgba(196,72,72,0.08)', border: '1px solid rgba(196,72,72,0.15)', marginBottom: '12px', borderRadius: '8px' }}>
              <p className="font-sans text-sm" style={{ color: '#c44' }}>{retryError}</p>
              <p className="font-sans text-xs mt-1" style={{ color: '#8c8782' }}>Your previous design is still showing. Try another style below.</p>
            </div>
          )}

          <h2 style={{ fontFamily: 'var(--serif)', fontSize: '26px', color: '#1F1D1B', lineHeight: 1.2, marginBottom: '10px' }}>
            Your site is <em style={{ fontStyle: 'italic', color: '#C9A84C' }}>ready.</em>
          </h2>
          <p style={{ fontFamily: 'var(--sans)', fontSize: '14px', color: '#6B6660', lineHeight: 1.6, marginBottom: '20px' }}>
            {isLoggedIn
              ? 'Save this design and start customizing with Iris.'
              : 'Create a free account to customize everything, add your photos, and go live.'}
          </p>

          {/* PRIMARY CTA */}
          <button
            className="btn-primary"
            style={{ width: '100%', textAlign: 'center', marginBottom: '8px', animation: 'shimmerBtn 2s ease-in-out infinite' }}
            data-testid="demo-subscribe-cta"
            onClick={handleSignup}
          >
            {isLoggedIn ? 'Save & Open Builder' : 'Keep This Design'}
          </button>
          {!isLoggedIn && !showAuth && (
            <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#8c8782', textAlign: 'center', marginBottom: '24px' }}>
              Free account. No credit card needed.
            </p>
          )}

          {/* ── INLINE AUTH FORM (slides in below CTA) ── */}
          {showAuth && !isLoggedIn && (
            <div data-testid="inline-auth" style={{ marginBottom: '20px', padding: '20px', background: '#fff', borderRadius: '12px', border: '1px solid #e8e3dc', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', animation: 'fadeIn 0.3s ease' }}>
              <form onSubmit={handleInlineAuth}>
                {authMode === 'signup' && (
                  <input
                    type="text" value={authName} onChange={e => { setAuthName(e.target.value); setAuthError(''); }}
                    placeholder="Your name"
                    data-testid="inline-auth-name"
                    style={{ width: '100%', padding: '12px 14px', fontFamily: 'var(--sans)', fontSize: '14px', background: '#faf8f5', border: '1px solid #e8e3dc', color: '#2a2725', borderRadius: '10px', outline: 'none', marginBottom: '10px', transition: 'border-color .2s' }}
                    autoComplete="name"
                  />
                )}
                <input
                  type="email" value={authEmail} onChange={e => { setAuthEmail(e.target.value); setAuthError(''); }}
                  placeholder="Email" required
                  data-testid="inline-auth-email"
                  style={{ width: '100%', padding: '12px 14px', fontFamily: 'var(--sans)', fontSize: '14px', background: '#faf8f5', border: '1px solid #e8e3dc', color: '#2a2725', borderRadius: '10px', outline: 'none', marginBottom: '10px', transition: 'border-color .2s' }}
                  autoComplete="email" autoFocus
                />
                <div style={{ position: 'relative', marginBottom: '10px' }}>
                  <input
                    type={showPw ? 'text' : 'password'} value={authPassword} onChange={e => { setAuthPassword(e.target.value); setAuthError(''); }}
                    placeholder={authMode === 'login' ? 'Password' : 'Create a password (6+ chars)'} required minLength={6}
                    data-testid="inline-auth-password"
                    style={{ width: '100%', padding: '12px 14px', paddingRight: '44px', fontFamily: 'var(--sans)', fontSize: '14px', background: '#faf8f5', border: '1px solid #e8e3dc', color: '#2a2725', borderRadius: '10px', outline: 'none', transition: 'border-color .2s' }}
                    autoComplete={authMode === 'login' ? 'current-password' : 'new-password'}
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#8c8782', padding: '2px' }}>
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                {authError && <p data-testid="inline-auth-error" style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: '#c47272', marginBottom: '10px' }}>{authError}</p>}

                <button type="submit" disabled={authLoading} data-testid="inline-auth-submit" style={{
                  width: '100%', padding: '14px', fontFamily: 'var(--sans)', fontSize: '14px', fontWeight: 600,
                  background: '#C9A84C', color: '#fff', border: 'none', borderRadius: '10px',
                  cursor: authLoading ? 'wait' : 'pointer', opacity: authLoading ? 0.7 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  boxShadow: '0 2px 8px rgba(201,168,76,0.2)',
                }}>
                  {authLoading ? <><Loader2 size={16} className="animate-spin" /> Saving your site...</> : (authMode === 'login' ? 'Sign In & Save' : 'Create Account & Save')}
                </button>
              </form>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: '#8c8782', textAlign: 'center', marginTop: '12px' }}>
                {authMode === 'signup' ? 'Already have an account? ' : "Don't have an account? "}
                <button onClick={() => { setAuthMode(authMode === 'signup' ? 'login' : 'signup'); setAuthError(''); }}
                  style={{ background: 'none', border: 'none', color: '#C9A84C', cursor: 'pointer', fontSize: '12px', fontFamily: 'var(--sans)' }}>
                  {authMode === 'signup' ? 'Sign in' : 'Sign up'}
                </button>
              </p>
            </div>
          )}

          {/* Feature list */}
          {!isLoggedIn && !showAuth && (
            <div className="space-y-2.5 mb-6" style={{ paddingTop: '8px', borderTop: '1px solid #e8e3dc' }}>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', fontWeight: 600, color: '#8c8782', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: '8px' }}>What you get</p>
              {['Upload your own photos & videos', 'Refine with Iris AI chat', 'Custom fonts, colors, and domain', 'Lead inbox + analytics'].map((f, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: '#C9A84C' }} />
                  <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: '#6B6660' }}>{f}</p>
                </div>
              ))}
            </div>
          )}

          {/* Try different style */}
          <button onClick={() => setShowRetry(!showRetry)} data-testid="demo-retry-toggle" style={{
            width: '100%', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            fontFamily: 'var(--sans)', fontSize: '12px', fontWeight: 500, color: '#8c8782',
            background: 'none', border: '1px solid #e8e3dc', borderRadius: '10px', cursor: 'pointer',
            transition: 'all 0.15s', marginBottom: '8px',
          }}>
            <ChevronDown size={14} style={{ transform: showRetry ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            Try a different style
          </button>

          {showRetry && (
            <div style={{ marginBottom: '16px', opacity: regenerating ? 0.5 : 1, pointerEvents: regenerating ? 'none' : 'auto', animation: 'fadeIn 0.3s ease' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <input type="text" defaultValue="" placeholder="Describe a vibe..."
                  className="w-full px-3 py-2 rounded-lg text-sm font-sans border outline-none transition-all focus:border-gh-gold"
                  style={{ background: '#f5f2ee', borderColor: '#e8e3dc', color: '#2a2725', fontSize: '12px', borderRadius: '10px' }}
                  id="retry-vibe-input"
                  onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) onRetry(e.target.value.trim()); }}
                />
                <button onClick={() => { const el = document.getElementById('retry-vibe-input'); const v = el?.value?.trim(); if (v) onRetry(v); }}
                  className="px-4 py-2 text-xs font-sans font-medium transition-all flex-shrink-0"
                  style={{ background: '#C9A84C', color: '#fff', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', borderRadius: '10px' }}>
                  Go
                </button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                {ALL_RETRY_STYLES.map(v => (
                  <button key={v.label} onClick={() => handleRetryPreset(v.prompt)}
                    className="px-2.5 py-1.5 text-xs font-sans transition-all"
                    style={{ color: '#8c8782', background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.06)', cursor: 'pointer', fontSize: '11px', lineHeight: '1.4', borderRadius: '9999px' }}
                    onMouseEnter={e => { e.currentTarget.style.color = '#C9A84C'; e.currentTarget.style.background = 'rgba(201,168,76,0.1)'; e.currentTarget.style.borderColor = 'rgba(201,168,76,0.3)'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = '#8c8782'; e.currentTarget.style.background = 'rgba(0,0,0,0.03)'; e.currentTarget.style.borderColor = 'rgba(0,0,0,0.06)'; }}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <button onClick={onClose} className="w-full text-center text-xs font-sans font-medium py-2"
            style={{ color: '#8c8782', background: 'none', border: 'none', cursor: 'pointer', marginTop: '8px' }} data-testid="demo-back-to-site">
            Back to GoldenHour
          </button>
        </div>

        {/* RIGHT: Site Preview */}
        <div className={`${resultTab === 'info' ? 'hidden lg:flex' : 'flex'} flex-1 flex-col`} style={{ background: 'rgba(242,239,233,0.03)' }}>
          <div className="flex items-center px-4 py-2 flex-shrink-0" style={{ background: '#11100F', borderBottom: '1px solid #292725' }}>
            <div className="flex gap-1.5 mr-3">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#FF605C' }} />
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#FFBD44' }} />
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#00CA4E' }} />
            </div>
            <span className="text-xs font-sans" style={{ color: '#6B6B76' }}>
              {businessName.toLowerCase().replace(/\s+/g, '')}.goldenhour.build
            </span>
            <div className="ml-auto">
              <span className="text-xs font-sans font-medium px-3 py-1 rounded-full" style={{ background: 'rgba(201,168,76,0.08)', color: '#C9A84C', fontSize: '10px' }}>
                Preview
              </span>
            </div>
          </div>
          <div className="flex-1">
            <iframe
              srcDoc={generatedHtml + '<script>document.addEventListener("click",function(e){var a=e.target.closest("a");if(a){e.preventDefault();e.stopPropagation();var h=a.getAttribute("href");if(h&&h.startsWith("#")&&h.length>1){var t=document.querySelector(h);if(t)t.scrollIntoView({behavior:"smooth"});}return false;}},true);document.addEventListener("submit",function(e){e.preventDefault();},true);Object.defineProperty(window,"location",{configurable:false,set:function(){}});window.open=function(){return null;};<\/script>'}
              title="Generated Site Preview"
              className="w-full h-full border-0"
              sandbox="allow-scripts allow-same-origin"
              data-testid="demo-preview-iframe"
            />
          </div>
        </div>
      </div>
    </div>
  );
});

ResultView.displayName = 'ResultView';
export default ResultView;
